import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ENUMS, PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

/**
 * @author Burak Ak
 *
 */

const TierBasedPriceForm = NiceModal.create(({ id, modalName, mode }) => {
	const { Currency } = useLocale();
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();
	const defaultColSpan = {
		md: 6
	};

	const [isLoading, setIsLoading] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					destinationTier: yup.string().nullable().required(),
					originTier: yup.string().nullable().required(),
					price: yup.string().nullable().required(),
					shippingContractID: yup.number().nullable().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			destinationTier: null,
			originTier: null,
			price: null,
			shippingContractID: null,
			currency: Currency
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const fillForm = useCallback(() => {
		setIsLoading(true);
		axiosPrivate
			.post(PRICE_CONFIGURATIONS.GET_TIER_BASED_PRICE, { id })
			.then((response) => {
				const { data, status } = response;

				if (status === 200) {
					reset({
						destinationTier: data.destinationTier ?? null,
						originTier: data.originTier ?? null,
						price: data.price ?? null,
						shippingContractID: data.shippingContract.id,
						currency: data?.shippingContract?.currency ?? undefined
					});
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [id]);

	useEffect(() => {
		if (id) fillForm();
	}, [id]);

	const onSubmit = (data) => {
		setIsLoading(true);

		const url = PRICE_CONFIGURATIONS.ADD_TIER_BASED_PRICE;
		axiosPrivate
			.post(url, {
				id: id ?? null,
				destinationTier: data.destinationTier,
				originTier: data.originTier,
				price: data.price,
				shippingContract: {
					id: data.shippingContractID
				}
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.PRICE_CONFIGURATIONS_TIER_BASED_PRICES));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const fetchAutocompleteData = useCallback(async (props) => {
		const data = await axiosPrivate
			.post(PRICE_CONFIGURATIONS.GET_SHIPPING_CONTRACT_AUTOCOMPLETE_LIST)
			.catch((error) => {
				showError();
				AppLogger.error(error);
			});

		return data.data.filter((item) =>
			props?.keyword
				? item?.name?.toLowerCase().includes(props?.keyword.toLowerCase()) ||
					item?.deliveryCompanySettingsId?.toString().toLowerCase().includes(props?.keyword.toLowerCase())
				: props?.IDs
					? props?.IDs.includes(item.ID)
					: true
		);
	}, []);

	const shippingContractAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'deliveryCompanySettingsId', width: 50 },
			{ accessor: 'companyName', width: 100 },
			{ accessor: 'name', width: 200 }
		],
		[]
	);

	return (
		<Modal fullwidth name={modalName} maxWidth="medium" className="modal-tierBasedPriceForm">
			<ModalTitle>
				<Typography variant="p1">tierBasedPriceForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="shippingContract" isLoading={isLoading} required>
						<AutoComplete
							fetchFunction={fetchAutocompleteData}
							columns={shippingContractAutocompleteColumns}
							value={watch('shippingContractID')}
							setValue={(value) => {
								setValue('shippingContractID', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue('currency', value?.currency);
							}}
							name={`shippingContractID`}
							selectionLabelAccessor={['deliveryCompanySettingsId', 'name']}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="originTier" isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							name="originTier"
							enumName={ENUMS.PRICE_CONFIGURATION_TIER_TYPE}
							setValue={(value) => {
								setValue(`originTier`, value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch(`originTier`)}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="destinationTier" isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							name="destinationTier"
							enumName={ENUMS.PRICE_CONFIGURATION_TIER_TYPE}
							setValue={(value) => {
								setValue(`destinationTier`, value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch(`destinationTier`)}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="price" isLoading={isLoading} required>
						<IntegerInput name="price" allowFloat decimalLimit={2} unit={watch(`currency`)} />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />

				<Button
					loading={isLoading}
					label={mode === 'edit' ? 'save' : 'button.add'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default TierBasedPriceForm;
